import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import {
  useCreateEquipmentNotificationMutation,
  useCreateLcertNotificationMutation,
  useDeleteEquipmentNotificationMutation,
  useDeleteLcertNotificationMutation,
  useEquipmentNotificationsQuery,
  useGetEquipmentNotificationQuery,
  useGetLcertNotificationQuery,
  useLcertNotificationsQuery,
  useOrganizationsQuery,
  useRepCountiesQuery,
  useUpdateEquipmentNotificationMutation,
  useUpdateLcertNotificationMutation,
} from '@services/index';
import type { NotificationBody, NotificationContact } from '@/types';
import { NOTIFICATION_DOMAINS, type NotificationDomain } from './domain';

/**
 * Hooks bundle for one notification domain. Equipment contacts carry the
 * organization in `?organizationId=`, which every page in that domain keeps
 * on its links.
 */
export const useNotificationDomain = (domain: NotificationDomain, id?: number) => {
  const config = NOTIFICATION_DOMAINS[domain];
  const [params] = useSearchParams();
  const organizationId =
    domain === 'equipment' ? Number(params.get('organizationId')) || undefined : undefined;
  const query = organizationId ? `?organizationId=${organizationId}` : '';

  const lcertList = useLcertNotificationsQuery(undefined, { skip: domain !== 'lcert' });
  // No organization (Equipment tab on "Show All") lists every contact, like
  // the legacy /equipment/0/notifications.
  const equipmentList = useEquipmentNotificationsQuery(
    { organizationId },
    { skip: domain !== 'equipment' },
  );
  const lcertOne = useGetLcertNotificationQuery(id as number, { skip: domain !== 'lcert' || !id });
  const equipmentOne = useGetEquipmentNotificationQuery(
    { id: id as number, organizationId },
    { skip: domain !== 'equipment' || !id },
  );
  const counties = useRepCountiesQuery(undefined, { skip: domain !== 'lcert' });
  const organizations = useOrganizationsQuery(undefined, { skip: domain !== 'equipment' });

  const [createLcert, createLcertState] = useCreateLcertNotificationMutation();
  const [updateLcert, updateLcertState] = useUpdateLcertNotificationMutation();
  const [deleteLcert, deleteLcertState] = useDeleteLcertNotificationMutation();
  const [createEquipment, createEquipmentState] = useCreateEquipmentNotificationMutation();
  const [updateEquipment, updateEquipmentState] = useUpdateEquipmentNotificationMutation();
  const [deleteEquipment, deleteEquipmentState] = useDeleteEquipmentNotificationMutation();

  const create = useCallback(
    (body: NotificationBody): Promise<NotificationContact> =>
      domain === 'lcert'
        ? createLcert(body).unwrap()
        : createEquipment({ organizationId, body }).unwrap(),
    [domain, organizationId, createLcert, createEquipment],
  );
  const update = useCallback(
    (rowId: number, body: NotificationBody): Promise<NotificationContact> =>
      domain === 'lcert'
        ? updateLcert({ id: rowId, body }).unwrap()
        : updateEquipment({ id: rowId, organizationId, body }).unwrap(),
    [domain, organizationId, updateLcert, updateEquipment],
  );
  const remove = useCallback(
    (rowId: number) =>
      domain === 'lcert'
        ? deleteLcert(rowId).unwrap()
        : deleteEquipment({ id: rowId, organizationId }).unwrap(),
    [domain, organizationId, deleteLcert, deleteEquipment],
  );

  const triggerOptions = useMemo(
    () =>
      domain === 'lcert'
        ? (counties.data || []).map((g) => g.pvGroupName)
        : (organizations.data || []).map((o) => o.pvOrganization),
    [domain, counties.data, organizations.data],
  );

  const list = domain === 'lcert' ? lcertList : equipmentList;
  const one = domain === 'lcert' ? lcertOne : equipmentOne;

  return {
    config,
    organizationId,
    /** Query string to keep on every link inside the domain. */
    query,
    listPath: `${config.basePath}${query}`,
    viewPath: (rowId: number) => `${config.basePath}/${rowId}${query}`,
    editPath: (rowId: number) => `${config.basePath}/${rowId}/edit${query}`,
    newPath: `${config.basePath}/new${query}`,
    rows: list.data || [],
    listLoading: list.isLoading,
    listError: list.isError,
    refetchList: list.refetch,
    row: one.data,
    rowLoading: one.isLoading || one.isFetching,
    rowError: one.isError,
    triggerOptions,
    create,
    update,
    remove,
    saving:
      createLcertState.isLoading ||
      updateLcertState.isLoading ||
      createEquipmentState.isLoading ||
      updateEquipmentState.isLoading,
    deleting: deleteLcertState.isLoading || deleteEquipmentState.isLoading,
  };
};
